import type { ZoneBuilder } from '../types';
import { scatterPeople } from '../crowd';
import { scatterForest } from '../forest';
import { hillLat } from '../hills';
import { GLACIAL_LAKE, LENGA, MORAINE, PATAGONIA_ROOFS, PATAGONIA_WOOD, darken } from '../palettes';

/**
 * scenery/zones/patagonia.ts — cold blue twilight, the legendary finale.
 * A frontier town of bright-roofed cabins → the crimson lenga forest →
 * the milky turquoise lake with icebergs adrift → snowfields beneath the
 * glacier, with Fitz Roy waiting at the line. The lake lies on side +1.
 */

export const LAKE_SIDE = 1;

/** Warm fieldstone for the cabin chimneys. The old 0x8a8478 was a cold pale
 *  grey — MEASURED against the town silhouette it was the BRIGHTEST thing on
 *  the skyline (brighter than the timber walls) under a dark twilight sky,
 *  which is what made a row of them read as concrete smokestacks. This sits
 *  warmer AND a step darker than the cabin walls, so the stacks settle into
 *  the town instead of leading it. */
const CHIMNEY_STONE = 0x7d6a5a;
/** The cap slab that closes the flue — darker again, so the stack ends in a
 *  masonry lip instead of an open pipe mouth. */
const CHIMNEY_CAP = 0x574a3f;

/** How much saturation comes out of the frontier roof palette (patagonia-ts).
 *  The saturated flat fills read as toy tent tarps on the START GRID — the
 *  finale's first frame — and they swamped the shared roof-course texture.
 *  Muted at constant luminance, the corrugation finally shows through and the
 *  bright-roof frontier identity survives. */
const ROOF_MUTE = 0.15;

/** Desaturate toward the colour's own luminance. Applied to the PICKED value
 *  only — the shared PATAGONIA_ROOFS table in palettes.ts is untouched. */
function muteRoof(c: number): number {
  const r = (c >> 16) & 255;
  const g = (c >> 8) & 255;
  const b = c & 255;
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  const mix = (v: number): number => Math.round(v + (lum - v) * ROOF_MUTE);
  return (mix(r) << 16) | (mix(g) << 8) | mix(b);
}

const elchalten: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a }) => {
  // Small timber cabins under bright pyramid roofs, ranch fences at the kerb.
  if (rng.next() < 0.7) {
    const w = rng.range(6, 9);
    const h = rng.range(3.5, 5.5);
    const d = rng.range(6, 9);
    const lat = hw + 3 + w / 2 + rng.range(0, 6);
    const [x, y, z] = at(lat);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color: rng.pick(PATAGONIA_WOOD) });
    const roof = muteRoof(rng.pick(PATAGONIA_ROOFS));
    a.roofs.push({ x, y: y + h, z, rotY: s.dir, w: w + 1.6, h: 2.6, d: d + 1.6, color: roof });
    // EAVE / FASCIA BAND (patagonia-ts). buildRoofs' pyramid meets the wall in a
    // zero-thickness edge, so the overhang read as folded paper. This band sits
    // just under the roof base and 0.075 proud of it on every side: a real
    // fascia depth plus the shadow line it throws down the timber.
    a.plain.push({
      x,
      y: y + h - 0.34,
      z,
      rotY: s.dir,
      w: w + 1.75,
      h: 0.34,
      d: d + 1.75,
      color: darken(roof, 0.52),
    });
    // The frontier signatures (M14): a stone CHIMNEY trailing up past the
    // eaves, a covered timber PORCH on posts, and the firewood stack against
    // the wall — every El Chaltén cabin earns its winter.
    // patagonia-ts: the stack was h+2.8 tall in cold flat grey — massed along
    // the town skyline that reads as concrete smokestacks over the cabins
    // (measured on the start grid). h+1.5 clears the ridge and stops there,
    // in warm fieldstone, capped.
    const [cx2, cy2, cz2] = at(lat + w / 2 - 0.9);
    const chBase = cy2 + h * 0.5 + 1.4;
    const chH = h + 1.5;
    a.plain.push({ x: cx2, y: chBase, z: cz2, rotY: s.dir, w: 1.0, h: chH, d: 1.0, color: CHIMNEY_STONE }); // stone chimney
    a.plain.push({ x: cx2, y: chBase + chH, z: cz2, rotY: s.dir, w: 1.34, h: 0.3, d: 1.34, color: CHIMNEY_CAP }); // cap slab
    const [px2, py2, pz2] = at(lat - w / 2 - 1.1);
    a.plain.push({ x: px2, y: py2 + 2.5, z: pz2, rotY: s.dir, w: 2.5, h: 0.18, d: d * 0.7, color: 0x4a3424 }); // porch roof
    a.plain.push({ x: px2, y: py2 + 1.25, z: pz2, rotY: s.dir, w: 0.16, h: 2.5, d: 0.16, color: 0x4a3424 }); // porch post
    if (rng.next() < 0.6) {
      const [wx2, wy2, wz2] = at(lat - w / 2 - 0.45);
      a.plain.push({ x: wx2, y: wy2 + 0.5, z: wz2, rotY: s.dir, w: 0.8, h: 1.0, d: 2.2, color: 0x6a4e36 }); // firewood stack
    }
  }
  if (rng.next() < 0.55) {
    const [x, y, z] = at(hw + 1.8);
    a.fences.push({ x, y, z, rotY: s.dir });
  }
  if (rng.next() < 0.25) {
    const [x, y, z] = at(hw + 4 + rng.range(0, 8));
    a.pines.push({ x, y, z, rotY: 0, s: rng.range(0.8, 1.2), color: rng.pick([0x2c5a3c, 0x35684a]) });
  }
  // A few hikers/villagers at the trailhead town (the rest of Patagonia stays
  // empty wilderness on purpose).
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.8, hw + 5], chance: 0.3, count: 2 });
};

const lenga: ZoneBuilder = ({ rng, hw, at, s, a }) => {
  // The southern beech turns crimson — a forest on fire with autumn. M67 (owner):
  // a forest you can SEE THROUGH — individual cascaded lengas with gaps, the
  // front row pulled back + small so nothing overhangs the road or curtains the
  // ball (it used to stack full-size trees right at the guardrail).
  scatterForest(rng, hw, at, a, {
    kind: 'pine',
    colors: LENGA,
    count: [2, 5],
    inner: 5,
    depth: 36,
    gap: 8,
    sizeNear: 0.8,
    sizeFar: 1.5,
  });
  if (rng.next() < 0.3) {
    // M98: moraine hills keep their edge off the road (same draws, clamped).
    const base = hw + 18 + rng.range(0, 36);
    const r = rng.range(18, 38);
    const [x, y, z] = at(hillLat(hw, base, r));
    a.hills.push({ x, y, z, r, h: rng.range(6, 16), color: rng.pick(MORAINE) });
  }
  if (rng.next() < 0.3) {
    const [x, y, z] = at(hw + 1.9);
    a.fences.push({ x, y, z, rotY: s.dir });
  }
};

const glaciallake: ZoneBuilder = ({ rng, hw, at, s, a, side, step }) => {
  if (side === LAKE_SIDE) {
    // The milky turquoise water, icebergs drifting out on it.
    const w = rng.range(60, 80);
    const [x, y, z] = at(hw + w / 2 + 1);
    a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: GLACIAL_LAKE, arc: s.distance, side, shore: hw + 1 });
    if (rng.next() < 0.3) {
      const [ix, iy, iz] = at(hw + 10 + rng.range(0, 30));
      a.icebergs.push({ x: ix, y: iy + 0.4, z: iz, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.8, 2.2) });
    }
  } else {
    // Moraine shore: lenga + bare hills. (M67: inner margin so a shore lenga's
    // canopy never overhangs the road.)
    if (rng.next() < 0.5) {
      const [x, y, z] = at(hw + 5 + rng.range(0, 20));
      a.pines.push({ x, y, z, rotY: 0, s: rng.range(0.8, 1.3), color: rng.pick(LENGA) });
    }
    if (rng.next() < 0.3) {
      const base = hw + 16 + rng.range(0, 30);
      const r = rng.range(16, 34);
      const [x, y, z] = at(hillLat(hw, base, r));
      a.hills.push({ x, y, z, r, h: rng.range(5, 14), color: rng.pick(MORAINE) });
    }
  }
};

const glacier: ZoneBuilder = ({ rng, hw, at, s, a, side, step }) => {
  if (side === LAKE_SIDE) {
    // Meltwater at the glacier's foot + stranded ice blocks.
    const w = rng.range(30, 44);
    const [x, y, z] = at(hw + w / 2 + 1);
    a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: 0x6ac8d8, arc: s.distance, side, shore: hw + 1 });
    if (rng.next() < 0.35) {
      const [ix, iy, iz] = at(hw + 6 + rng.range(0, 16));
      a.icebergs.push({ x: ix, y: iy + 0.4, z: iz, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.6, 1.4) });
    }
  } else {
    // Snowfields rising toward the peaks.
    if (rng.next() < 0.4) {
      const base = hw + 14 + rng.range(0, 34);
      const r = rng.range(18, 40);
      const [x, y, z] = at(hillLat(hw, base, r));
      a.hills.push({ x, y, z, r, h: rng.range(6, 18), color: rng.pick([0xdfe8ee, 0xd0dde6, 0xc2d2dc]) });
    }
    if (rng.next() < 0.25) {
      const [x, y, z] = at(hw + 5 + rng.range(0, 12)); // M67: inner margin, no road overhang
      a.pines.push({ x, y, z, rotY: 0, s: rng.range(0.7, 1.1), color: 0x2c5a3c });
    }
  }
};

export const PATAGONIA_ZONES = { elchalten, lenga, glaciallake, glacier };
